import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import api from './utils/api';
import LicenseScreen from './pages/license/LicenseScreen';
import AppInstallPrompt from './components/AppInstallPrompt';

// Pages
import LoginPage from './pages/LoginPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import ProjectsPage from './pages/admin/ProjectsPage';
import MainPOListPage from './pages/admin/MainPOListPage';
import MainPOFormPage from './pages/admin/MainPOFormPage';
import QuotationListPage from './pages/admin/QuotationListPage';
import QuotationFormPage from './pages/admin/QuotationFormPage';
import FinishListPage from './pages/admin/FinishListPage';
import FinishSheetPage from './pages/admin/FinishSheetPage';
import CashBookPage from './pages/admin/CashBookPage';
import PayrollPage from './pages/admin/PayrollPage';
import QualityReportListPage from './pages/admin/QualityReportListPage';
import QualityReportPage from './pages/admin/QualityReportPage';
import CFTListPage from './pages/admin/CFTListPage';
import CFTSheetPage from './pages/admin/CFTSheetPage';
import MyCftSheetsPage from './pages/worker/MyCftSheetsPage';
import ProjectDetailPage from './pages/admin/ProjectDetailPage';
import WorkersPage from './pages/admin/WorkersPage';
import DepartmentsPage from './pages/admin/DepartmentsPage';
import TasksPage from './pages/admin/TasksPage';
import ReportsPage from './pages/admin/ReportsPage';
import WorkerLayout from './pages/worker/WorkerLayout';
import WorkerDashboard from './pages/worker/WorkerDashboard';
import WorkerTasksPage from './pages/worker/WorkerTasksPage';
import WorkerPackingPage from './pages/worker/WorkerPackingPage';
import WorkerDispatchPage from './pages/worker/WorkerDispatchPage';
import AdminPackingPage from './pages/admin/AdminPackingPage';
import ProductTrackingPage from './pages/admin/ProductTrackingPage';
import TimeReportPage from './pages/admin/TimeReportPage';
import DailyProgressPage from './pages/admin/DailyProgressPage';
import MaterialPage from './pages/admin/MaterialPage';
import AdminQueriesPage from './pages/admin/AdminQueriesPage';
import WorkerQueriesPage from './pages/worker/WorkerQueriesPage';
import SettingsPage from './pages/admin/SettingsPage';
import TermsConditionsPage from './pages/admin/TermsConditionsPage';
// Ask Moji — hidden for now (to turn on: remove the // on the Ask Moji lines)
// import AskMojiPage from './pages/admin/AskMojiPage';
// import ClientAskMojiPage from './pages/client/ClientAskMojiPage';
import { SettingsProvider } from './context/SettingsContext';
import ClientLayout from './pages/client/ClientLayout';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientProjectsPage from './pages/client/ClientProjectsPage';
import ClientProjectDetailPage from './pages/client/ClientProjectDetailPage';
import ClientsPage from './pages/admin/ClientsPage';
import BarcodePage from './pages/admin/BarcodePage';
import PurchaseOrderPage from './pages/admin/PurchaseOrderPage';
import SaleChallanPage from './pages/admin/SaleChallanPage';
import AdminSalesInvoicePage from './pages/admin/AdminSalesInvoicePage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ClientPurchaseOrdersPage from './pages/admin/ClientPurchaseOrdersPage';
import ClientTicketsPage from './pages/admin/ClientTicketsPage';
import ClientPurchaseOrdersClientPage from './pages/client/ClientPurchaseOrdersPage';
import ClientTicketsClientPage from './pages/client/ClientTicketsPage';
import ClientSalesPage from './pages/client/ClientSalesPage';
import UserRightsPage from './pages/admin/UserRightsPage';
import ClientPOReportPage from './pages/admin/ClientPOReportPage';
import OutsourceTrackerPage from './pages/admin/OutsourceTrackerPage';
import ApprovalsPage from './pages/admin/ApprovalsPage';
import DashboardProjectOverviewPage from './pages/admin/DashboardProjectOverviewPage';
import DashboardItemsWaitingPage from './pages/admin/DashboardItemsWaitingPage';
import DashboardDispatchReportPage from './pages/admin/DashboardDispatchReportPage';
import DashboardOutsourcePage from './pages/admin/DashboardOutsourcePage';
import MyApprovalsPage from './pages/MyApprovalsPage';
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminPackagesPage from './pages/superadmin/SuperAdminPackagesPage';
import SuperAdminOrganizationsPage from './pages/superadmin/SuperAdminOrganizationsPage';
import WebsiteManagerPage from './pages/admin/WebsiteManagerPage';

// Public website
import PublicLayout from './pages/public/components/PublicLayout';
import HomePage from './pages/public/HomePage';
import ProductDetailPage from './pages/public/ProductDetailPage';
import ProductListingPage from './pages/public/ProductListingPage';
import CraftsmanshipPage from './pages/public/CraftsmanshipPage';
import MakersPage from './pages/public/MakersPage';
import WishlistPage from './pages/public/WishlistPage';
import PublicPage from './pages/public/PublicPage';
import PublicProjectsPage from './pages/public/ProjectsPage';

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (role && user.role !== role) {
    if (user.role === 'superadmin') return <Navigate to="/superadmin" />;
    if (user.role === 'admin') return <Navigate to="/admin" />;
    if (user.role === 'client') return <Navigate to="/client" />;
    return <Navigate to="/worker" />;
  }
  return children;
}

// Refreshes worker permissions on every route change — instant effect without logout
function RoutePermissionSync() {
  const location = useLocation();
  const { user, loadPermissions } = useAuth();
  useEffect(() => {
    if (user && user.role === 'worker') {
      loadPermissions();
    }
  }, [location.pathname]);
  return null;
}

// app.moji.org.in serves the software (login/dashboard); moji.org.in / www.moji.org.in serve the public website
const isAppDomain = typeof window !== 'undefined' && window.location.hostname === 'app.moji.org.in';

function AppRoutes({ licenseInfo, onLicenseActivated }) {
  const { user } = useAuth();
  // COMMENTED OUT - License block disabled, all users can access app
  // const blocked = licenseInfo && !['active', 'trial'].includes(licenseInfo.status);
  // if (blocked) {
  //   return <LicenseScreen licenseInfo={licenseInfo} onActivated={onLicenseActivated} />;
  // }

  const homeRedirect = user
    ? (user.role === 'superadmin' ? '/superadmin' : user.role === 'admin' ? '/admin' : user.role === 'client' ? '/client' : '/worker')
    : '/login';

  return (
    <>
      <RoutePermissionSync />
      <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={user.role === 'superadmin' ? '/superadmin' : user.role === 'admin' ? '/admin' : user.role === 'client' ? '/client' : '/worker'} />} />
      <Route path="/license" element={<LicenseScreen licenseInfo={licenseInfo} onActivated={onLicenseActivated} />} />

      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout licenseInfo={licenseInfo} /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        {/* <Route path="ask-moji" element={<AskMojiPage />} /> */}
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="main-po" element={<MainPOListPage />} />
        <Route path="main-po/new" element={<MainPOFormPage />} />
        <Route path="main-po/:id" element={<MainPOFormPage />} />
        <Route path="finish" element={<FinishListPage />} />
        <Route path="quotations" element={<QuotationListPage />} />
        <Route path="quotations/:id" element={<QuotationFormPage />} />
        <Route path="main-po/:id/finish/:type" element={<FinishSheetPage />} />
        <Route path="cash-book" element={<CashBookPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="quality" element={<QualityReportListPage />} />
        <Route path="quality/:id" element={<QualityReportPage />} />
        <Route path="cft" element={<CFTListPage />} />
        <Route path="cft/:id" element={<CFTSheetPage />} />
        <Route path="workers" element={<WorkersPage />} />
        <Route path="user-rights" element={<UserRightsPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="client-po-report" element={<ClientPOReportPage />} />
        <Route path="product-tracking" element={<ProductTrackingPage />} />
        <Route path="time-report" element={<TimeReportPage />} />
        <Route path="daily-progress" element={<DailyProgressPage />} />
        <Route path="materials" element={<MaterialPage />} />
        <Route path="packing" element={<AdminPackingPage />} />
        <Route path="queries" element={<AdminQueriesPage />} />
        <Route path="website" element={<WebsiteManagerPage />} />
        <Route path="terms" element={<TermsConditionsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="barcodes" element={<BarcodePage />} />
        <Route path="purchase-orders" element={<PurchaseOrderPage />} />
        <Route path="sale-challans" element={<SaleChallanPage />} />
        <Route path="sales" element={<AdminSalesInvoicePage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="client-pos" element={<ClientPurchaseOrdersPage />} />
        <Route path="client-tickets" element={<ClientTicketsPage />} />
        <Route path="outsource" element={<OutsourceTrackerPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="dashboard/project-overview" element={<DashboardProjectOverviewPage />} />
        <Route path="dashboard/items-waiting"    element={<DashboardItemsWaitingPage />} />
        <Route path="dashboard/dispatch-report"  element={<DashboardDispatchReportPage />} />
        <Route path="dashboard/outsource"        element={<DashboardOutsourcePage />} />
      </Route>

      {/* SUPER ADMIN */}
      <Route path="/superadmin" element={<ProtectedRoute role="superadmin"><SuperAdminLayout /></ProtectedRoute>}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="organizations" element={<SuperAdminOrganizationsPage />} />
        <Route path="packages" element={<SuperAdminPackagesPage />} />
      </Route>

      {/* CLIENT PORTAL */}
      <Route path="/client" element={<ProtectedRoute role="client"><ClientLayout /></ProtectedRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="projects" element={<ClientProjectsPage />} />
        <Route path="projects/:id" element={<ClientProjectDetailPage />} />
        <Route path="purchase-orders" element={<ClientPurchaseOrdersClientPage />} />
        <Route path="support" element={<ClientTicketsClientPage />} />
        <Route path="sales" element={<ClientSalesPage />} />
        {/* <Route path="ask-moji" element={<ClientAskMojiPage />} /> */}
      </Route>

      <Route path="/worker" element={<ProtectedRoute role="worker"><WorkerLayout licenseInfo={licenseInfo} /></ProtectedRoute>}>
        <Route index element={<WorkerDashboard />} />
        {/* Always-visible worker pages */}
        <Route path="my-tasks"        element={<WorkerTasksPage />} />
        <Route path="queries"         element={<WorkerQueriesPage />} />
        {/* Legacy routes kept for backward compat */}
        <Route path="tasks"           element={<WorkerTasksPage />} />
        {/* Permission-gated — reuse admin pages */}
        <Route path="projects"        element={<ProjectsPage />} />
        <Route path="projects/:id"    element={<ProjectDetailPage />} />
        <Route path="main-po"         element={<MainPOListPage />} />
        <Route path="main-po/new"     element={<MainPOFormPage />} />
        <Route path="main-po/:id"     element={<MainPOFormPage />} />
        <Route path="finish"          element={<FinishListPage />} />
        <Route path="quotations"      element={<QuotationListPage />} />
        <Route path="quotations/:id"  element={<QuotationFormPage />} />
        <Route path="main-po/:id/finish/:type" element={<FinishSheetPage />} />
        <Route path="cash-book"       element={<CashBookPage />} />
        <Route path="payroll"         element={<PayrollPage />} />
        <Route path="quality"         element={<QualityReportListPage />} />
        <Route path="quality/:id"     element={<QualityReportPage />} />
        <Route path="cft"             element={<CFTListPage />} />
        <Route path="cft/:id"         element={<CFTSheetPage />} />
        <Route path="my-cft"          element={<MyCftSheetsPage />} />
        <Route path="my-cft/:id"      element={<CFTSheetPage />} />
        <Route path="tasks-admin"     element={<TasksPage />} />
        <Route path="workers"         element={<WorkersPage />} />
        <Route path="departments"     element={<DepartmentsPage />} />
        <Route path="clients"         element={<ClientsPage />} />
        <Route path="reports"         element={<ReportsPage />} />
        <Route path="client-po-report" element={<ClientPOReportPage />} />
        <Route path="product-tracking" element={<ProductTrackingPage />} />
        <Route path="time-report"     element={<TimeReportPage />} />
        <Route path="daily-progress"  element={<DailyProgressPage />} />
        <Route path="materials"       element={<MaterialPage />} />
        <Route path="packing"         element={<WorkerPackingPage />} />
        <Route path="barcodes"        element={<BarcodePage />} />
        <Route path="dispatch"        element={<WorkerDispatchPage />} />
        <Route path="sales"           element={<AdminSalesInvoicePage />} />
        <Route path="sale-challans"   element={<SaleChallanPage />} />
        <Route path="client-pos"      element={<ClientPurchaseOrdersPage />} />
        <Route path="client-support"  element={<ClientTicketsPage />} />
        <Route path="outsource"       element={<OutsourceTrackerPage />} />
        <Route path="queries-admin"   element={<AdminQueriesPage />} />
        <Route path="terms"           element={<TermsConditionsPage />} />
        <Route path="settings"        element={<SettingsPage />} />
        <Route path="my-approvals"    element={<MyApprovalsPage />} />
      </Route>

      <Route path="/" element={isAppDomain ? <Navigate to={homeRedirect} /> : <PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="products" element={<ProductListingPage />} />
        <Route path="products/:slug" element={<ProductDetailPage />} />
        <Route path="craftsmanship" element={<CraftsmanshipPage />} />
        <Route path="projects" element={<PublicProjectsPage />} />
        <Route path="makers" element={<MakersPage />} />
        <Route path="wishlist" element={<WishlistPage />} />
        {/* Any other admin-created page (About, Dealers, Hospitality, Terms, etc.) */}
        <Route path=":slug" element={<PublicPage />} />
      </Route>
      <Route path="*" element={<Navigate to={isAppDomain ? homeRedirect : '/'} />} />
    </Routes>
    </>
  );
}

function LicenseBanner({ licenseInfo }) {
  if (!licenseInfo || licenseInfo.status === 'active') return null;
  if (licenseInfo.status !== 'trial') return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-slate-900 text-center py-1.5 text-xs font-semibold">
      ⏰ Trial Mode — {licenseInfo.days_left} day{licenseInfo.days_left !== 1 ? 's' : ''} remaining
      <a href="/license" className="ml-3 underline font-bold">Purchase Now</a>
    </div>
  );
}

function App() {
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [checking, setChecking] = useState(false); // DISABLED - License check commented out

  const checkLicense = useCallback(async () => {
    try {
      // COMMENTED OUT - License check disabled
      // const res = await api.get('/license/info');
      // setLicenseInfo(res.data);
      setLicenseInfo({ status: 'active' }); // Always allow access
    } catch {
      // Backend unreachable - allow offline
      setLicenseInfo(prev => prev || { status: 'active' });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // COMMENTED OUT - License check on mount disabled
    // checkLicense();
    setLicenseInfo({ status: 'active' }); // Always allow access
    setChecking(false);

    // COMMENTED OUT - Periodic license check disabled
    // Check every 2 minutes - will block immediately
    // const interval = setInterval(checkLicense, 2 * 60 * 1000);
    // return () => clearInterval(interval);
  }, [checkLicense]);

  // COMMENTED OUT - Visibility change license check disabled
  // useEffect(() => {
  //   const handleVisibility = () => {
  //     if (document.visibilityState === 'visible') {
  //       checkLicense();
  //     }
  //   };
  //   document.addEventListener('visibilitychange', handleVisibility);
  //   return () => document.removeEventListener('visibilitychange', handleVisibility);
  // }, [checkLicense]);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Checking license...</p>
        </div>
      </div>
    );
  }

  return (
    <SettingsProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration: 3000,
          style: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155' }
        }} />
        {licenseInfo?.status === 'trial' && <LicenseBanner licenseInfo={licenseInfo} />}
        <div className={licenseInfo?.status === 'trial' ? 'pt-7' : ''}>
          <AppRoutes licenseInfo={licenseInfo} onLicenseActivated={checkLicense} />
        </div>
        <AppInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
